import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  posApi,
  getSessionToken,
  operationKey,
  subscribePOSUpdates,
} from "./api";
import {
  BOOTSTRAP_KEY,
  CART_KEY,
  cacheDelete,
  cacheGet,
  cacheSet,
} from "./cache";
import { printerFor } from "./printer";
import { StockRefreshGate, PENDING_CHECKOUT_KEY, readPendingCheckout, persistCheckout } from "./stock-refresh";
import type {
  BootstrapData,
  CartLine,
  OperationResult,
  PaymentInput,
  PrintJob,
  ProductItem,
  TicketPage,
} from "./types";

const money = (value: number) =>
  new Intl.NumberFormat("es-MX", { style: "currency", currency: "MXN" }).format(
    value || 0,
  );
const num = (value: unknown) => Number(value || 0);

async function executePrint(
  config: BootstrapData["printer"],
  job: PrintJob,
  drawerOnly = false,
) {
  try {
    const adapter = printerFor(config);
    const result = drawerOnly
      ? await adapter.openDrawer(job)
      : await adapter.print(job);
    void posApi.completePOSPrint(job.id, true).catch(() => undefined);
    return result;
  } catch (error) {
    void posApi
      .completePOSPrint(job.id, false, (error as Error).message)
      .catch(() => undefined);
    throw error;
  }
}

function printTicketA4(detail: Record<string, unknown>) {
  const ticket = detail.ticket as
    | {
        sale?: Record<string, unknown>;
        lines?: Array<Record<string, unknown>>;
        payments?: Array<Record<string, unknown>>;
      }
    | undefined;
  const sale = ticket?.sale || {};
  const escape = (value: unknown) =>
    String(value ?? "").replace(
      /[&<>"']/g,
      (character) =>
        ({
          "&": "&amp;",
          "<": "&lt;",
          ">": "&gt;",
          '"': "&quot;",
          "'": "&#39;",
        })[character] || character,
    );
  const popup = window.open("", "_blank");
  if (!popup)
    throw new Error("Permite la ventana emergente para guardar el PDF.");
  popup.opener = null;
  popup.document.write(
    `<!doctype html><meta charset="utf-8"><title>${escape(sale.folio || "Ticket")}</title><style>@import url("https://fonts.googleapis.com/css2?family=Cinzel:wght@700&family=Fraunces:wght@400;600&display=swap");body{font:400 16px "Fraunces",Georgia,serif;max-width:760px;margin:40px auto;padding:20px;color:#222}header{text-align:center}h1{font-family:"Cinzel","Times New Roman",serif;font-weight:700}table{width:100%;border-collapse:collapse;margin:24px 0}td,th{padding:10px;border-bottom:1px solid #ddd;text-align:left}th,strong,b{font-weight:600}.money{text-align:right}footer{text-align:center;margin-top:40px;color:#555}@media print{body{margin:0}}</style><header><h1>Casa Hawa</h1><h2>${escape(sale.folio)}</h2><p>${escape(sale.soldAt || "")}</p></header><p>Cliente: ${escape(sale.customer || "Público general")}<br>Atendió: ${escape(sale.attendant || "")}</p><table><thead><tr><th>Producto</th><th>Cantidad</th><th class="money">Importe</th></tr></thead><tbody>${(ticket?.lines || []).map((line) => `<tr><td>${escape(line.name)}</td><td>${escape(line.units)}</td><td class="money">${money(num(line.total))}</td></tr>`).join("")}</tbody></table><h2 class="money">Total ${money(num(sale.total))}</h2><p>${(ticket?.payments || []).map((payment) => `${escape(payment.method)}: ${money(num(payment.amount))}`).join("<br>")}</p><footer>Comprobante no fiscal<br>Gracias por tu compra</footer>`,
  );
  popup.document.close();
  popup.focus();
  window.setTimeout(() => popup.print(), 100);
}

function Login({ onLogin }: { onLogin: () => Promise<void> }) {
  const [name, setName] = useState("");
  const [pin, setPin] = useState("");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  return (
    <main className="login-shell">
      <form
        className="login-card"
        onSubmit={async (event) => {
          event.preventDefault();
          setBusy(true);
          setError("");
          try {
            await posApi.loginPOS(name, pin);
            await cacheDelete(BOOTSTRAP_KEY);
            await cacheDelete(CART_KEY);
            await onLogin();
          } catch (e) {
            setError((e as Error).message);
          } finally {
            setBusy(false);
          }
        }}
      >
        <img className="brand-login-logo" src="/brand/stacked.svg" alt="Casa Hawa" width="1082" height="602" fetchPriority="high" />
        <h1 className="visually-hidden">Casa Hawa</h1>
        <p>Punto de venta</p>
        <label>
          Tu nombre
          <input
            autoFocus
            autoComplete="username"
            value={name}
            onChange={(e) => setName(e.target.value)}
            required
          />
        </label>
        <label>
          Tu PIN
          <input
            type="password"
            inputMode="numeric"
            pattern="[0-9]{6,12}"
            autoComplete="current-password"
            value={pin}
            onChange={(e) => setPin(e.target.value)}
            required
          />
        </label>
        {error && <div className="error">{error}</div>}
        <button className="primary" disabled={busy}>
          {busy ? "Comprobando…" : "Entrar"}
        </button>
        <small>Versión 4.1.0 · Solo personal autorizado</small>
      </form>
    </main>
  );
}

function ProductGrid({
  products,
  query,
  category,
  add,
}: {
  products: ProductItem[];
  query: string;
  category: string;
  add: (item: ProductItem) => void;
}) {
  const normalized = query.trim().toLocaleLowerCase("es-MX");
  const visible = products.filter(
    (item) =>
      (!category || item.categoryId === category) &&
      (!normalized ||
        [item.productName, item.presentationName, item.code, ...item.barcodes]
          .join(" ")
          .toLocaleLowerCase("es-MX")
          .includes(normalized)),
  );
  return (
    <div className="product-grid">
      {visible.map((item) => (
        <button
          key={item.skuId}
          className="product-card"
          disabled={item.stock <= 0}
          onClick={() => add(item)}
        >
          <span className="product-name">{item.productName}</span>
          <span>{item.presentationName}</span>
          <code>{item.code}</code>
          <strong>{money(item.price)}</strong>
          <em className={item.stock <= 3 ? "low" : ""}>
            {item.stock} disponibles
          </em>
        </button>
      ))}
      {!visible.length && (
        <div className="empty">No encuentro productos con esa búsqueda.</div>
      )}
    </div>
  );
}

function Cart({
  lines,
  change,
  total,
}: {
  lines: CartLine[];
  change: (skuId: string, units: number) => void;
  total: number;
}) {
  return (
    <section className="cart">
      <header>
        <h2>Venta</h2>
        <strong>
          {lines.reduce((sum, line) => sum + line.units, 0)} artículos
        </strong>
      </header>
      <div className="cart-lines">
        {lines.map((line) => (
          <article key={line.skuId}>
            <div>
              <b>{line.productName}</b>
              <small>
                {line.presentationName} · {line.code}
              </small>
            </div>
            <div className="quantity">
              <button onClick={() => change(line.skuId, line.units - 1)}>
                −
              </button>
              <span>{line.units}</span>
              <button
                onClick={() => change(line.skuId, line.units + 1)}
                disabled={line.units >= line.stock}
              >
                +
              </button>
            </div>
            <strong>{money(line.price * line.units)}</strong>
          </article>
        ))}
        {!lines.length && (
          <div className="empty">Escanea o toca un producto para empezar.</div>
        )}
      </div>
      <footer>
        <span>Total</span>
        <strong>{money(total)}</strong>
      </footer>
    </section>
  );
}

function PaymentDialog({
  data,
  total,
  onClose,
  onPaid,
}: {
  data: BootstrapData;
  total: number;
  onClose: () => void;
  onPaid: (payments: PaymentInput[], customerId?: string) => Promise<void>;
}) {
  type Draft = PaymentInput & { rowId: string };
  const firstMethod = data.paymentMethods[0];
  const [rows, setRows] = useState<Draft[]>([
    {
      rowId: crypto.randomUUID(),
      methodId: firstMethod?.id || "",
      amount: total,
      received: total,
      reference: "",
    },
  ]);
  const [busy, setBusy] = useState(false);
  const [customers, setCustomers] = useState(data.customers);
  const [customerId, setCustomerId] = useState("");
  const paid = rows.reduce((sum, row) => sum + num(row.amount), 0);
  const remaining = Math.round((total - paid) * 100) / 100;
  const update = (rowId: string, values: Partial<Draft>) =>
    setRows((current) =>
      current.map((row) => (row.rowId === rowId ? { ...row, ...values } : row)),
    );
  const valid =
    rows.length > 0 &&
    Math.abs(remaining) < 0.009 &&
    new Set(rows.map((row) => row.methodId)).size === rows.length &&
    rows.every((row) => {
      const method = data.paymentMethods.find(
        (item) => item.id === row.methodId,
      );
      return (
        row.amount > 0 &&
        (method?.type !== "EFECTIVO" || num(row.received) >= row.amount) &&
        (!method?.requiresReference || !!row.reference?.trim())
      );
    });
  return (
    <div className="overlay">
      <div className="dialog">
        <button className="close" onClick={onClose}>
          ×
        </button>
        <h2>Cobrar {money(total)}</h2>
        <div className="customer-row">
          <select
            value={customerId}
            onChange={(event) => setCustomerId(event.target.value)}
          >
            <option value="">Público general</option>
            {customers.map((customer) => (
              <option key={customer.id} value={customer.id}>
                {customer.name}
                {customer.phone ? ` · ${customer.phone}` : ""}
              </option>
            ))}
          </select>
          <button
            onClick={async () => {
              const query = prompt("Nombre, teléfono o correo del cliente");
              if (query == null) return;
              const result = await posApi.searchPOSCustomers(query);
              setCustomers(result.items);
            }}
          >
            Buscar
          </button>
          <button
            onClick={async () => {
              const name = prompt("Nombre del cliente");
              if (!name) return;
              const phone = prompt("Teléfono (opcional)") || "";
              const email = prompt("Correo (opcional)") || "";
              try {
                const result = await posApi.savePOSCustomer({
                  idempotencyKey: operationKey("customer"),
                  name,
                  phone,
                  email,
                });
                setCustomers((current) => [
                  result.customer,
                  ...current.filter((item) => item.id !== result.customer.id),
                ]);
                setCustomerId(result.customer.id);
              } catch (error) {
                alert((error as Error).message);
              }
            }}
          >
            Nuevo
          </button>
        </div>
        <div className="payment-rows">
          {rows.map((row, index) => {
            const method = data.paymentMethods.find(
              (item) => item.id === row.methodId,
            );
            return (
              <section className="payment-row" key={row.rowId}>
                <select
                  value={row.methodId}
                  onChange={(e) =>
                    update(row.rowId, {
                      methodId: e.target.value,
                      received: row.amount,
                      reference: "",
                    })
                  }
                >
                  {data.paymentMethods.map((item) => (
                    <option key={item.id} value={item.id}>
                      {item.name}
                    </option>
                  ))}
                </select>
                <input
                  aria-label="Importe"
                  type="number"
                  min="0.01"
                  step="0.01"
                  value={row.amount}
                  onChange={(e) =>
                    update(row.rowId, {
                      amount: num(e.target.value),
                      received:
                        method?.type === "EFECTIVO"
                          ? Math.max(num(row.received), num(e.target.value))
                          : num(e.target.value),
                    })
                  }
                />
                {method?.type === "EFECTIVO" && (
                  <input
                    aria-label="Recibido"
                    type="number"
                    min={row.amount}
                    step="0.01"
                    value={row.received}
                    onChange={(e) =>
                      update(row.rowId, { received: num(e.target.value) })
                    }
                  />
                )}
                {method?.requiresReference && (
                  <input
                    placeholder="Referencia"
                    value={row.reference}
                    onChange={(e) =>
                      update(row.rowId, { reference: e.target.value })
                    }
                  />
                )}
                {rows.length > 1 && (
                  <button
                    onClick={() =>
                      setRows((current) =>
                        current.filter((item) => item.rowId !== row.rowId),
                      )
                    }
                  >
                    ×
                  </button>
                )}
                {index === rows.length - 1 && remaining !== 0 && (
                  <button
                    onClick={() =>
                      update(row.rowId, {
                        amount: Math.max(
                          0.01,
                          Math.round((row.amount + remaining) * 100) / 100,
                        ),
                      })
                    }
                  >
                    Completar
                  </button>
                )}
              </section>
            );
          })}
        </div>
        <button
          disabled={rows.length >= data.paymentMethods.length}
          onClick={() => {
            const unused = data.paymentMethods.find(
              (method) => !rows.some((row) => row.methodId === method.id),
            );
            if (unused)
              setRows((current) => [
                ...current,
                {
                  rowId: crypto.randomUUID(),
                  methodId: unused.id,
                  amount: Math.max(0.01, remaining),
                  received: Math.max(0.01, remaining),
                  reference: "",
                },
              ]);
          }}
        >
          Dividir pago
        </button>
        <div className="change">
          {remaining > 0 ? (
            <>
              Falta: <strong>{money(remaining)}</strong>
            </>
          ) : remaining < 0 ? (
            <>
              Sobra: <strong>{money(-remaining)}</strong>
            </>
          ) : (
            <>Pagos completos</>
          )}
        </div>
        <button
          className="charge"
          disabled={busy || !valid}
          onClick={async () => {
            setBusy(true);
            try {
              await onPaid(
                rows.map(({ rowId: _rowId, ...row }) => row),
                customerId || undefined,
              );
            } finally {
              setBusy(false);
            }
          }}
        >
          {busy ? "Guardando…" : `Cobrar ${money(total)}`}
        </button>
        <p className="safe-note">
          El cobro solo termina cuando aparece el folio. Si la red falla, puedes
          repetirlo sin duplicarlo.
        </p>
      </div>
    </div>
  );
}

function Tickets({
  shiftId,
  role,
  printer,
  onClose,
}: {
  shiftId: string;
  role: BootstrapData["user"]["role"];
  printer: BootstrapData["printer"];
  onClose: () => void;
}) {
  const [query, setQuery] = useState("");
  const [page, setPage] = useState<TicketPage>({ items: [], nextCursor: null });
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [detail, setDetail] = useState<Record<string, unknown> | null>(null);
  const search = useCallback(
    async (cursor?: string) => {
      setBusy(true);
      setError("");
      try {
        const result = await posApi.searchPOSSales(query, shiftId, cursor);
        setPage((previous) =>
          cursor
            ? {
                items: [...previous.items, ...result.items],
                nextCursor: result.nextCursor,
              }
            : result,
        );
      } catch (reason) {
        setError((reason as Error).message || "No se pudieron cargar los tickets.");
      } finally {
        setBusy(false);
      }
    },
    [query, shiftId],
  );
  useEffect(() => {
    void search();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps
  return (
    <div className="overlay">
      <div className="dialog wide">
        <button className="close" onClick={onClose}>
          ×
        </button>
        <h2>Tickets del turno</h2>
        <form
          className="ticket-search"
          onSubmit={(e) => {
            e.preventDefault();
            void search();
          }}
        >
          <input
            placeholder="Folio, producto, código, cliente o vendedor"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
          />
          <button>Buscar</button>
        </form>
        {error && <div className="error">{error}</div>}
        {busy && !page.items.length ? (
          <div className="loading-inline">Buscando tickets…</div>
        ) : (
          <div className="ticket-list">
            {page.items.map((ticket) => (
              <button
                key={ticket.id}
                onClick={async () => {
                  setBusy(true);
                  setError("");
                  try {
                    setDetail(await posApi.getPOSSaleTicket(ticket.id));
                  } catch (reason) {
                    setError((reason as Error).message || "No se pudo abrir el ticket.");
                  } finally {
                    setBusy(false);
                  }
                }}
              >
                <span>
                  <b>{ticket.folio}</b>
                  <small>
                    {ticket.customer} · {ticket.attendant}
                  </small>
                  <small>{ticket.products.join(" · ")}</small>
                </span>
                <strong>{money(ticket.total - ticket.refund)}</strong>
              </button>
            ))}
          </div>
        )}
        {!busy && !error && !page.items.length && (
          <div className="empty">No hay tickets que coincidan.</div>
        )}
        {page.nextCursor && (
          <button
            disabled={busy}
            onClick={() => void search(page.nextCursor || undefined)}
          >
            Cargar más
          </button>
        )}
        {detail && (
          <TicketDetail
            detail={detail}
            role={role}
            printer={printer}
            close={() => setDetail(null)}
            returned={() => {
              setDetail(null);
              void search();
            }}
          />
        )}
      </div>
    </div>
  );
}

function TicketDetail({
  detail,
  role,
  printer,
  close,
  returned,
}: {
  detail: Record<string, unknown>;
  role: BootstrapData["user"]["role"];
  printer: BootstrapData["printer"];
  close: () => void;
  returned: () => void;
}) {
  const ticket = detail.ticket as
    | {
        sale?: { id?: string; folio?: string; total?: number };
        lines?: Array<{
          id: string;
          name: string;
          units: number;
          returnedUnits?: number;
          total: number;
        }>;
      }
    | undefined;
  const [busy, setBusy] = useState(false);
  const [quantities, setQuantities] = useState<Record<string, number>>({});
  const [reenter, setReenter] = useState<Record<string, boolean>>({});
  const legacy = detail.legacy === true;
  const printJob = detail.printJob as PrintJob | null | undefined;
  const returnLines = (ticket?.lines || []).flatMap((line) => {
    const available = line.units - num(line.returnedUnits);
    const units = Math.min(
      available,
      Math.max(0, Math.floor(num(quantities[line.id]))),
    );
    return units > 0
      ? [
          {
            saleLineId: line.id,
            units,
            reenterInventory: role === "ADMIN" && reenter[line.id] === true,
          },
        ]
      : [];
  });
  return (
    <div className="subdialog">
      <button className="close" onClick={close}>
        ×
      </button>
      <h3>{ticket?.sale?.folio || "Ticket"}</h3>
      {ticket?.lines?.map((line) => {
        const available = line.units - num(line.returnedUnits);
        return (
          <div className="return-line" key={line.id}>
            <span>
              {line.units} × {line.name}
              <small>{available} disponibles para devolver</small>
            </span>
            <strong>{money(line.total)}</strong>
            {!legacy && available > 0 && (
              <input
                aria-label={`Devolver ${line.name}`}
                type="number"
                min="0"
                max={available}
                step="1"
                value={quantities[line.id] || 0}
                onChange={(event) =>
                  setQuantities((current) => ({
                    ...current,
                    [line.id]: Math.min(
                      available,
                      Math.max(0, Math.floor(num(event.target.value))),
                    ),
                  }))
                }
              />
            )}
            {!legacy && role === "ADMIN" && available > 0 && (
              <label>
                <input
                  type="checkbox"
                  checked={reenter[line.id] === true}
                  onChange={(event) =>
                    setReenter((current) => ({
                      ...current,
                      [line.id]: event.target.checked,
                    }))
                  }
                />{" "}
                Unidad cerrada y apta: reingresar
              </label>
            )}
          </div>
        );
      })}
      <h3>Total {money(ticket?.sale?.total || 0)}</h3>
      <button
        onClick={() => {
          try {
            printTicketA4(detail);
          } catch (error) {
            alert((error as Error).message);
          }
        }}
      >
        Imprimir / guardar PDF A4
      </button>
      {printJob && (
        <button
          disabled={busy}
          onClick={async () => {
            setBusy(true);
            try {
              const result = await posApi.retryPOSPrint(printJob.id);
              const printed = await executePrint(printer, result.printJob);
              alert(printed.message);
            } catch (error) {
              alert((error as Error).message);
            } finally {
              setBusy(false);
            }
          }}
        >
          Reimprimir copia
        </button>
      )}
      {legacy ? (
        <p className="safe-note">
          Ticket histórico de consulta. Sus devoluciones anteriores permanecen
          en Sheets.
        </p>
      ) : (
        <button
          className="danger"
          disabled={busy || !returnLines.length}
          onClick={async () => {
            const reason = prompt("Motivo de la devolución");
            if (!reason) return;
            const adminName = prompt("Nombre del administrador");
            const adminPin = prompt("PIN del administrador");
            if (!adminName || !adminPin) return;
            setBusy(true);
            try {
              const result = await posApi.returnPOSSale({
                idempotencyKey: operationKey("return"),
                saleId: ticket?.sale?.id,
                lines: returnLines,
                reason,
                adminName,
                adminPin,
              });
              if (!result?.ok || !result.operationId)
                throw new Error("El servidor no confirmó la devolución.");
              if (result.printJob) await executePrint(printer, result.printJob);
              alert(result.message || "Devolución guardada.");
              returned();
            } catch (e) {
              alert((e as Error).message);
            } finally {
              setBusy(false);
            }
          }}
        >
          {busy ? "Devolviendo…" : "Guardar devolución seleccionada"}
        </button>
      )}
    </div>
  );
}

function Transfers({
  data,
  reload,
  close,
}: {
  data: BootstrapData;
  reload: () => Promise<void>;
  close: () => void;
}) {
  const [busy, setBusy] = useState(false);
  const [destinationId, setDestinationId] = useState("");
  const [units, setUnits] = useState<Record<string, number>>({});
  const availableDestinations = data.locations.filter(
    (location) => location.id !== data.location.id,
  );
  const createTransfer = async () => {
    const lines = Object.entries(units).flatMap(([skuId, quantity]) =>
      quantity > 0 ? [{ skuId, units: Math.floor(quantity) }] : [],
    );
    if (!destinationId || !lines.length)
      return alert("Elige destino y al menos un producto.");
    setBusy(true);
    try {
      const result = await posApi.createPOSTransfer({
        idempotencyKey: operationKey("transfer"),
        originLocationId: data.location.id,
        destinationLocationId: destinationId,
        lines,
        notes: "Traspaso creado desde POS 4.1",
      });
      alert(result.message || "Traspaso enviado.");
      setUnits({});
      await reload();
    } catch (error) {
      alert((error as Error).message);
    } finally {
      setBusy(false);
    }
  };
  return (
    <div className="overlay">
      <div className="dialog wide">
        <button className="close" onClick={close}>
          ×
        </button>
        <h2>Traspasos</h2>
        {data.user.role !== "VENDEDOR" && (
          <section className="transfer-create">
            <h3>Enviar desde {data.location.name}</h3>
            <select
              value={destinationId}
              onChange={(event) => setDestinationId(event.target.value)}
            >
              <option value="">Elige destino</option>
              {availableDestinations.map((location) => (
                <option key={location.id} value={location.id}>
                  {location.name}
                </option>
              ))}
            </select>
            <div className="transfer-products">
              {data.products
                .filter((product) => product.stock > 0)
                .map((product) => (
                  <label key={product.skuId}>
                    <span>
                      {product.productName} · {product.presentationName}
                      <small>{product.stock} disponibles</small>
                    </span>
                    <input
                      type="number"
                      min="0"
                      max={product.stock}
                      step="1"
                      value={units[product.skuId] || 0}
                      onChange={(event) =>
                        setUnits((current) => ({
                          ...current,
                          [product.skuId]: Math.min(
                            product.stock,
                            Math.max(0, Math.floor(num(event.target.value))),
                          ),
                        }))
                      }
                    />
                  </label>
                ))}
            </div>
            <button
              className="primary"
              disabled={busy}
              onClick={() => void createTransfer()}
            >
              Preparar y enviar
            </button>
          </section>
        )}
        <section>
          <h3>Pendientes</h3>
          {(data.transfers || []).map((transfer) => (
            <article className="transfer-card" key={transfer.id}>
              <b>
                {transfer.origin?.name} → {transfer.destination?.name}
              </b>
              <small>{transfer.status}</small>
              {transfer.lines.map((line) => (
                <p key={line.id}>
                  <span>
                    {line.name} · {line.code}
                  </span>
                  <strong>
                    {line.receivedUnits ?? "—"} / {line.sentUnits}
                  </strong>
                </p>
              ))}
              {transfer.status === "IN_TRANSIT" &&
                transfer.destination?.id === data.location.id && (
                  <button
                    disabled={busy}
                    onClick={async () => {
                      const lines: Array<{
                        transferLineId: string;
                        receivedUnits: number;
                      }> = [];
                      for (const line of transfer.lines) {
                        const answer = prompt(
                          `¿Cuántas unidades llegaron de ${line.name}?`,
                          String(line.sentUnits),
                        );
                        if (answer == null) return;
                        lines.push({
                          transferLineId: line.id,
                          receivedUnits: Math.floor(num(answer)),
                        });
                      }
                      setBusy(true);
                      try {
                        const result = await posApi.receivePOSTransfer({
                          idempotencyKey: operationKey("receive"),
                          transferId: transfer.id,
                          lines,
                        });
                        alert(result.message || "Recepción guardada.");
                        await reload();
                      } catch (error) {
                        alert((error as Error).message);
                      } finally {
                        setBusy(false);
                      }
                    }}
                  >
                    Confirmar lo recibido
                  </button>
                )}
              {transfer.status === "DIFFERENCE" &&
                data.user.role === "ADMIN" && (
                  <button
                    className="danger"
                    disabled={busy}
                    onClick={async () => {
                      const choice = (
                        prompt(
                          "Escribe DEVOLVER para regresar la diferencia al origen o PERDIDA para registrarla como pérdida",
                        ) || ""
                      ).toUpperCase();
                      const resolution =
                        choice === "DEVOLVER"
                          ? "RETURN_TO_ORIGIN"
                          : choice === "PERDIDA"
                            ? "LOSS"
                            : "";
                      if (!resolution) return;
                      setBusy(true);
                      try {
                        const result =
                          await posApi.resolvePOSTransferDifference({
                            idempotencyKey: operationKey("resolve"),
                            transferId: transfer.id,
                            resolution,
                          });
                        alert(result.message || "Diferencia resuelta.");
                        await reload();
                      } catch (error) {
                        alert((error as Error).message);
                      } finally {
                        setBusy(false);
                      }
                    }}
                  >
                    Resolver diferencia
                  </button>
                )}
            </article>
          ))}
          {!(data.transfers || []).length && (
            <div className="empty">No hay traspasos pendientes.</div>
          )}
        </section>
      </div>
    </div>
  );
}

function ShiftPanel({
  shiftId,
  close,
}: {
  shiftId: string;
  close: () => void;
}) {
  const [summary, setSummary] = useState<Record<string, any> | null>(null);
  const [error, setError] = useState("");
  useEffect(() => {
    void posApi
      .getPOSPanel(shiftId)
      .then(setSummary)
      .catch((reason) => setError((reason as Error).message));
  }, [shiftId]);
  const ranking = (key: string) =>
    (summary?.[key] || []).map((row: { name: string; value: number }) => (
      <p key={row.name}>
        <span>{row.name}</span>
        <strong>
          {key === "paymentMethods" ? money(row.value) : row.value}
        </strong>
      </p>
    ));
  return (
    <div className="overlay">
      <div className="dialog wide">
        <button className="close" onClick={close}>
          ×
        </button>
        <h2>Resumen del turno</h2>
        {error && <div className="error">{error}</div>}
        {!summary ? (
          <div className="loading-inline">Calculando…</div>
        ) : (
          <>
            <div className="panel-stats">
              <div>
                <small>Ventas netas</small>
                <strong>{money(num(summary.net))}</strong>
              </div>
              <div>
                <small>Tickets</small>
                <strong>{num(summary.tickets)}</strong>
              </div>
              <div>
                <small>Ticket promedio</small>
                <strong>{money(num(summary.averageTicket))}</strong>
              </div>
              <div>
                <small>Unidades netas</small>
                <strong>{num(summary.units)}</strong>
              </div>
              <div>
                <small>Devoluciones</small>
                <strong>{money(num(summary.refunds))}</strong>
              </div>
              <div>
                <small>Efectivo esperado</small>
                <strong>{money(num(summary.expectedCash))}</strong>
              </div>
              {summary.utility !== undefined && (
                <div>
                  <small>Utilidad</small>
                  <strong>{money(num(summary.utility))}</strong>
                </div>
              )}
            </div>
            <div className="panel-columns">
              <section>
                <h3>Métodos de pago</h3>
                {ranking("paymentMethods")}
              </section>
              <section>
                <h3>Productos</h3>
                {ranking("products")}
              </section>
              <section>
                <h3>Quién atendió</h3>
                {ranking("attendants")}
              </section>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

export function App() {
  const [data, setData] = useState<BootstrapData | null>(null);
  const [cart, setCart] = useState<CartLine[]>([]);
  const [query, setQuery] = useState("");
  const [category, setCategory] = useState("");
  const [paying, setPaying] = useState(false);
  const [tickets, setTickets] = useState(false);
  const [transfers, setTransfers] = useState(false);
  const [panelOpen, setPanelOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState("");
  const [online, setOnline] = useState(navigator.onLine);
  const [saleDiscount, setSaleDiscount] = useState({
    type: "MXN",
    value: 0,
    reason: "",
    authorizationId: "",
  });
  const searchRef = useRef<HTMLInputElement>(null);
  const currentOperation = useRef("");
  const stockGate = useRef(new StockRefreshGate());
  const activeLocation = useRef<string | undefined>(undefined);
  const checkoutRunning = useRef(false);
  const [stockPending, setStockPending] = useState(true);
  const [pendingCheckout, setPendingCheckout] = useState(() => Boolean(localStorage.getItem(PENDING_CHECKOUT_KEY)));
  const focusScanner = () => {
    if (!window.matchMedia("(pointer: coarse)").matches) {
      window.setTimeout(() => searchRef.current?.focus({ preventScroll: true }), 0);
    }
  };
  const load = useCallback(async (locationId?: string, initial = false) => {
    const token = getSessionToken();
    if (!token) return;
    const sequence = stockGate.current.begin();
    if (locationId) activeLocation.current = locationId;
    setStockPending(true);
    const cached = initial ? await cacheGet<BootstrapData & { session?: string }>(BOOTSTRAP_KEY) : null;
    if (getSessionToken() !== token || !stockGate.current.accepts(sequence)) return;
    if (cached?.session === token && !locationId) {
      setData(cached);
      activeLocation.current = cached.location.id;
    }
    const fresh = await posApi.bootstrapPOS(locationId || activeLocation.current);
    if (getSessionToken() !== token || !stockGate.current.accepts(sequence)) return;
    activeLocation.current = fresh.location.id;
    setData(fresh);
    setStockPending(false);
    await cacheSet(BOOTSTRAP_KEY, {
      ...fresh,
      session: token,
      customers: [],
      transfers: [],
      databaseUsage: null,
    });
  }, []);
  useEffect(() => {
    void cacheGet<CartLine[]>(CART_KEY).then(
      (value) => value && !localStorage.getItem("casaHawaCartCleared") && setCart(value),
    );
    void load(undefined, true).catch((error) => {
      if (!data) setMessage((error as Error).message);
    });
    const refresh = () => { if (document.visibilityState !== "hidden") void load().catch(() => undefined); };
    const onOnline = () => { setOnline(true); refresh(); },
      onOffline = () => setOnline(false);
    window.addEventListener("online", onOnline);
    window.addEventListener("offline", onOffline);
    document.addEventListener("visibilitychange", refresh);
    return () => {
      window.removeEventListener("online", onOnline);
      window.removeEventListener("offline", onOffline);
      document.removeEventListener("visibilitychange", refresh);
      stockGate.current.invalidate();
    };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => {
    void cacheSet(CART_KEY, cart);
  }, [cart]);
  useEffect(() => {
    if (!data) return;
    let timer = 0;
    const unsubscribe = subscribePOSUpdates(() => {
      window.clearTimeout(timer);
      timer = window.setTimeout(() => void load().catch(() => undefined), 350);
    });
    return () => { window.clearTimeout(timer); unsubscribe(); };
  }, [data?.location.id, load]);
  // HID scanners may type without focusing an input on touch devices.
  useEffect(() => {
    if (!data || paying || tickets || transfers || panelOpen || pendingCheckout) return;
    let buffer = "", last = 0;
    const scan = (event: KeyboardEvent) => {
      const target = event.target as HTMLElement;
      if (target.closest("input,textarea,select,[contenteditable=true]") || event.ctrlKey || event.metaKey || event.altKey) return;
      if (event.key === "Enter" && buffer) { event.preventDefault(); setQuery(buffer); buffer = ""; return; }
      if (event.key.length !== 1) return;
      const now = Date.now();
      if (now - last > 120) buffer = "";
      buffer += event.key; last = now;
    };
    window.addEventListener("keydown", scan);
    return () => window.removeEventListener("keydown", scan);
  }, [data, paying, tickets, transfers, panelOpen, pendingCheckout]);
  useEffect(() => {
    const viewport = window.visualViewport;
    const resize = () => document.documentElement.style.setProperty("--pos-height", `${viewport?.height || window.innerHeight}px`);
    resize();
    viewport?.addEventListener("resize", resize);
    window.addEventListener("resize", resize);
    return () => { viewport?.removeEventListener("resize", resize); window.removeEventListener("resize", resize); };
  }, []);
  useEffect(() => {
    const exact = data?.products.find((item) =>
      [item.code, item.skuId, ...item.barcodes].some(
        (code) => code.toLocaleUpperCase() === query.trim().toLocaleUpperCase(),
      ),
    );
    if (exact && query.trim()) {
      add(exact);
      setQuery("");
    }
  }, [query]); // eslint-disable-line react-hooks/exhaustive-deps
  const subtotal = useMemo(
    () => cart.reduce((sum, line) => sum + line.price * line.units, 0),
    [cart],
  );
  const discountAmount =
    saleDiscount.type === "PCT"
      ? Math.round(subtotal * saleDiscount.value) / 100
      : saleDiscount.value;
  const total = Math.max(
    0,
    Math.round((subtotal - discountAmount) * 100) / 100,
  );
  const categories = useMemo(
    () =>
      Array.from(
        new Map(
          (data?.products || []).map((item) => [
            item.categoryId,
            item.categoryName,
          ]),
        ).entries(),
      ),
    [data],
  );
  if (!getSessionToken())
    return (
      <Login
        onLogin={async () => {
          setCart([]);
          await load();
        }}
      />
    );
  if (!data)
    return (
      <main className="splash">
        <img className="brand-splash-logo" src="/brand/stacked.svg" alt="Casa Hawa" width="1082" height="602" />
        <h1>Preparando tu punto de venta…</h1>
        <p>{message || "Recuperando catálogo y stock"}</p>
        <button onClick={() => void load()}>Reintentar</button>
      </main>
    );
  const add = (item: ProductItem) => {
    if (pendingCheckout || checkoutRunning.current || item.stock <= 0) return;
    localStorage.removeItem("casaHawaCartCleared");
    if (saleDiscount.authorizationId)
      setSaleDiscount({
        type: "MXN",
        value: 0,
        reason: "",
        authorizationId: "",
      });
    setCart((lines) => {
      const found = lines.find((line) => line.skuId === item.skuId);
      if (found)
        return lines.map((line) =>
          line.skuId === item.skuId
            ? { ...line, units: Math.min(line.stock, line.units + 1) }
            : line,
        );
      return [
        ...lines,
        { ...item, units: 1, discountType: "MXN", discountValue: 0 },
      ];
    });
    focusScanner();
  };
  const change = (skuId: string, units: number) =>
    !pendingCheckout && setCart((lines) =>
      lines.flatMap((line) =>
        line.skuId !== skuId ? [line] : units > 0 ? [{ ...line, units }] : [],
      ),
    );
  const discountContent = () => ({
    locationId: data?.location.id,
    lines: cart.map((line) => ({
      skuId: line.skuId,
      units: line.units,
      lineDiscountType: line.discountType,
      lineDiscountValue: line.discountValue,
    })),
    saleDiscountType: saleDiscount.type,
    saleDiscountValue: saleDiscount.value,
    reason: saleDiscount.reason,
  });
  const finishCheckout = async (
    payments: PaymentInput[] = [],
    customerId?: string,
  ) => {
    if (checkoutRunning.current) return;
    if (!navigator.onLine) throw new Error("Sin internet. Conservamos el cobro para reintentar.");
    const pending = readPendingCheckout(localStorage);
    if (pending && pending.userId !== data.user.id) throw new Error("Hay un cobro pendiente de otra persona. Esa persona debe recuperar su venta antes de iniciar otra.");
    if (pending && payments.length && pending.payload.idempotencyKey !== currentOperation.current) {
      setPendingCheckout(true);
      throw new Error("Hay otro cobro pendiente en este navegador. Usa Recuperar el mismo cobro antes de iniciar otro.");
    }
    if (!pending && !data?.shift) throw new Error("Primero abre la caja.");
    if (!currentOperation.current)
      currentOperation.current = operationKey("sale");
    const payload = pending?.payload || {
      idempotencyKey: currentOperation.current,
      locationId: data.location.id,
      shiftId: data.shift?.id,
      customerId,
      lines: cart.map((line) => ({
        skuId: line.skuId,
        units: line.units,
        discountType: line.discountType,
        discountValue: line.discountValue,
      })),
      payments,
      saleDiscountType: saleDiscount.type,
      saleDiscountValue: saleDiscount.value,
      discountReason: saleDiscount.reason,
      authorizationId: saleDiscount.authorizationId,
      authorizationContent: discountContent(),
    };
    persistCheckout(localStorage, { userId: data.user.id, payload });
    setPendingCheckout(true);
    checkoutRunning.current = true;
    stockGate.current.invalidate();
    setStockPending(true);
    let result: OperationResult;
    try {
      result = await posApi.checkoutPOS(payload as Parameters<typeof posApi.checkoutPOS>[0]);
    } catch (error) {
      if ((error as Error & { rejected?: boolean }).rejected) {
        localStorage.removeItem(PENDING_CHECKOUT_KEY);
        setPendingCheckout(false);
        currentOperation.current = "";
        void load().catch(() => undefined);
      }
      throw error;
    } finally {
      checkoutRunning.current = false;
    }
    if (!result?.ok || !result.operationId)
      throw new Error("El servidor no confirmó la venta.");
    setPaying(false);
    localStorage.setItem("casaHawaCartCleared", result.operationId);
    setCart([]);
    await cacheDelete(CART_KEY);
    localStorage.removeItem(PENDING_CHECKOUT_KEY);
    setPendingCheckout(false);
    setSaleDiscount({ type: "MXN", value: 0, reason: "", authorizationId: "" });
    currentOperation.current = "";
    focusScanner();
    // Never subtract from a snapshot that Realtime may already have updated.
    stockGate.current.invalidate();
    void load().catch(() => setStockPending(true));
    setMessage(
      `${result.folio || "Venta"} guardada${result.syncPending ? " · sincronizando con Sheets" : ""}.`,
    );
    if (result.printJob && data && !result.duplicate) {
      try {
        await executePrint(data.printer, result.printJob);
      } catch (error) {
        setMessage(
          `${result.folio} guardada. Impresión pendiente: ${(error as Error).message}`,
        );
      }
    }
  };
  const applyDiscount = async () => {
    const type = (
      prompt("Tipo de descuento: MXN o PCT", saleDiscount.type) || ""
    ).toUpperCase();
    if (!["MXN", "PCT"].includes(type)) return;
    const value = num(
      prompt(
        type === "PCT" ? "Porcentaje de descuento" : "Descuento en pesos",
        String(saleDiscount.value || 0),
      ),
    );
    if (
      !(value > 0) ||
      (type === "PCT" && value >= 100) ||
      (type === "MXN" && value >= subtotal)
    )
      return alert("El descuento no es válido.");
    const reason = prompt("Motivo obligatorio") || "";
    const adminName = prompt("Nombre del administrador") || "";
    const adminPin = prompt("PIN del administrador") || "";
    if (!reason || !adminName || !adminPin) return;
    const next = { type, value, reason, authorizationId: "" };
    const content = {
      locationId: data.location.id,
      lines: cart.map((line) => ({
        skuId: line.skuId,
        units: line.units,
        lineDiscountType: line.discountType,
        lineDiscountValue: line.discountValue,
      })),
      saleDiscountType: type,
      saleDiscountValue: value,
      reason,
    };
    try {
      const authorization = await posApi.authorizePOSAction({
        idempotencyKey: operationKey("discount"),
        action: "DISCOUNT",
        reason,
        adminName,
        adminPin,
        content,
      });
      setSaleDiscount({
        ...next,
        authorizationId: authorization.authorizationId,
      });
    } catch (e) {
      alert((e as Error).message);
    }
  };
  const openShift = async () => {
    const register = data.registers[0];
    if (!register) return alert("Configura una caja para esta ubicación.");
    const openingFloat = prompt("Fondo inicial de efectivo", "0");
    if (openingFloat == null) return;
    setBusy(true);
    try {
      const result = await posApi.openShift({
        idempotencyKey: operationKey("open"),
        locationId: data.location.id,
        registerId: register.id,
        openingFloat: num(openingFloat),
      });
      setMessage(result.message || "Caja abierta.");
      await load();
    } catch (e) {
      alert((e as Error).message);
    } finally {
      setBusy(false);
    }
  };
  const closeShift = async () => {
    if (!data.shift) return;
    const countedCash = prompt(
      "Cuenta el efectivo sin ver el esperado. ¿Cuánto hay?",
    );
    if (countedCash == null) return;
    setBusy(true);
    try {
      const result = await posApi.closeShift({
        idempotencyKey: operationKey("close"),
        locationId: data.location.id,
        shiftId: data.shift.id,
        countedCash: num(countedCash),
      });
      alert(result.message || "Caja cerrada.");
      await load();
    } catch (e) {
      alert((e as Error).message);
    } finally {
      setBusy(false);
    }
  };
  const registerCashMovement = async () => {
    if (!data.shift) return alert("Abre primero la caja.");
    const allowed =
      data.user.role === "VENDEDOR" ? "ENTRADA" : "ENTRADA, RETIRO o GASTO";
    const answer = (
      prompt(`Tipo de movimiento: ${allowed}`, "ENTRADA") || ""
    ).toUpperCase();
    const type =
      answer === "ENTRADA"
        ? "CASH_DEPOSIT"
        : answer === "RETIRO"
          ? "CASH_WITHDRAWAL"
          : answer === "GASTO"
            ? "CASH_EXPENSE"
            : "";
    if (!type) return alert("Tipo de movimiento no válido.");
    const amount = num(prompt("Importe en MXN"));
    if (!(amount > 0)) return alert("Escribe un importe positivo.");
    if (amount > 10_000_000)
      return alert("El importe no puede exceder $10,000,000 MXN.");
    const notes = prompt("Motivo o concepto") || "";
    if (!notes) return alert("Escribe el motivo.");
    setBusy(true);
    try {
      const result = await posApi.registerPOSCashMovement({
        idempotencyKey: operationKey("cash"),
        shiftId: data.shift.id,
        type,
        amount,
        notes,
      });
      setMessage(result.message || "Movimiento de caja guardado.");
    } catch (error) {
      alert((error as Error).message);
    } finally {
      setBusy(false);
    }
  };
  const openDrawer = async () => {
    const reason = prompt("Motivo de apertura sin venta");
    if (!reason) return;
    const adminName = prompt("Nombre del administrador");
    const adminPin = prompt("PIN del administrador");
    if (!adminName || !adminPin) return;
    setBusy(true);
    try {
      const result = await posApi.openCashDrawer({
        idempotencyKey: operationKey("drawer"),
        locationId: data.location.id,
        reason,
        adminName,
        adminPin,
      });
      if (!result.printJob) throw new Error("No se creó la orden del cajón.");
      const printed = await executePrint(data.printer, result.printJob, true);
      setMessage(printed.message);
    } catch (error) {
      alert((error as Error).message);
    } finally {
      setBusy(false);
    }
  };
  const quickCheckout = async (
    type: "EFECTIVO" | "TARJETA" | "TRANSFERENCIA",
  ) => {
    const method = data.paymentMethods.find((item) => item.type === type);
    if (!method) return alert(`No hay método ${type.toLowerCase()} activo.`);
    const reference = method.requiresReference
      ? prompt(`Referencia de ${method.name}`)
      : "";
    if (method.requiresReference && !reference) return;
    setBusy(true);
    try {
      await finishCheckout([
        {
          methodId: method.id,
          amount: total,
          received: type === "EFECTIVO" ? total : undefined,
          reference: reference || undefined,
        },
      ]);
    } catch (error) {
      alert((error as Error).message);
    } finally {
      setBusy(false);
    }
  };
  return (
    <main className="app">
      <header className="topbar">
        <div className="pos-brand">
          <img className="brand-header-logo" src="/brand/horizontal.svg" alt="Casa Hawa" width="1438" height="378" />
          <b className="visually-hidden">Casa Hawa POS</b>
          <small>
            {data.location.name} · {data.user.name}
          </small>
        </div>
        <div className="top-actions">
          {data.user.role !== "VENDEDOR" && data.locations.length > 1 && (
            <select
              aria-label="Ubicación activa"
              value={data.location.id}
              disabled={busy || pendingCheckout || Boolean(data.shift)}
              onChange={(event) => {
                if (
                  cart.length &&
                  !window.confirm(
                    "Cambiar de ubicación vaciará el carrito actual. ¿Continuar?",
                  )
                )
                  return;
                setCart([]);
                setCategory("");
                setQuery("");
                setBusy(true);
                void load(event.target.value)
                  .catch((error) => alert((error as Error).message))
                  .finally(() => setBusy(false));
              }}
            >
              {data.locations.map((location) => (
                <option key={location.id} value={location.id}>
                  {location.name}
                </option>
              ))}
            </select>
          )}
          <span className={online ? "online" : "offline"}>
            {online ? "En línea" : "Sin internet"}
          </span>
          {data.shift ? (
            <button disabled={pendingCheckout || busy} onClick={() => void closeShift()}>Cerrar caja</button>
          ) : (
            <button
              className="primary"
              disabled={busy}
              onClick={() => void openShift()}
            >
              Abrir caja
            </button>
          )}
          {data.shift && (
            <button onClick={() => setPanelOpen(true)}>Resumen</button>
          )}
          {data.shift && (
            <button disabled={busy} onClick={() => void registerCashMovement()}>
              Caja
            </button>
          )}
          <button disabled={busy} onClick={() => void openDrawer()}>
            Cajón
          </button>
          <button
            onClick={async () => {
              await posApi.logoutPOS();
              stockGate.current.invalidate();
              activeLocation.current = undefined;
              await cacheDelete(BOOTSTRAP_KEY);
              await cacheDelete(CART_KEY);
              setCart([]);
              setData(null);
            }}
          >
            Salir
          </button>
        </div>
      </header>
      {stockPending && <div className="notice" role="status">Existencias pendientes de actualización.</div>}
      {pendingCheckout && <div className="warning" role="status">Hay un cobro pendiente de confirmación. No vuelvas a cobrarlo.
        <button disabled={!online || busy} onClick={async () => {
          setBusy(true);
          try { await finishCheckout(); } catch (error) { alert((error as Error).message); }
          finally { setBusy(false); }
        }}>Recuperar el mismo cobro</button>
      </div>}
      {message && (
        <div className="notice" onClick={() => setMessage("")}>
          {message}
        </div>
      )}
      {data.databaseUsage?.warning && (
        <div className="warning">
          Supabase usa {Math.round(data.databaseUsage.ratio * 100)}% de su
          cuota.
          {data.databaseUsage.administrativeImportsBlocked
            ? " Las importaciones administrativas están pausadas; los cobros continúan."
            : " Conviene revisar el respaldo y el espacio disponible."}
        </div>
      )}
      <div className="workspace">
        <section className="catalog">
          <div className="search-row">
            <input
              ref={searchRef}
              autoFocus={!window.matchMedia("(pointer: coarse)").matches}
              placeholder="Buscar nombre, código o escanear"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
            />
            <button onClick={() => setTickets(true)}>Tickets</button>
            <button onClick={() => setTransfers(true)}>
              Traspasos
              {data.transfers?.length ? ` (${data.transfers.length})` : ""}
            </button>
          </div>
          <nav className="categories">
            <button
              className={!category ? "active" : ""}
              onClick={() => setCategory("")}
            >
              Todos
            </button>
            {categories.map(([id, name]) => (
              <button
                className={category === id ? "active" : ""}
                key={id}
                onClick={() => setCategory(id)}
              >
                {name}
              </button>
            ))}
          </nav>
          <ProductGrid
            products={data.products}
            query={query}
            category={category}
            add={add}
          />
        </section>
        <aside>
          <Cart
            lines={cart}
            change={(skuId, units) => {
              change(skuId, units);
              if (saleDiscount.authorizationId)
                setSaleDiscount({
                  type: "MXN",
                  value: 0,
                  reason: "",
                  authorizationId: "",
                });
            }}
            total={total}
          />
          {discountAmount > 0 && (
            <div className="discount-banner">
              Descuento autorizado: −{money(discountAmount)}
            </div>
          )}
          <button disabled={!cart.length || pendingCheckout} onClick={() => void applyDiscount()}>
            Descuento con Admin
          </button>
          <div className="quick-charge">
            <button
              disabled={
                busy || pendingCheckout ||
                !online ||
                !data.salesEnabled ||
                !data.shift ||
                !cart.length
              }
              onClick={() => void quickCheckout("EFECTIVO")}
            >
              Efectivo exacto
            </button>
            <button
              disabled={
                busy || pendingCheckout ||
                !online ||
                !data.salesEnabled ||
                !data.shift ||
                !cart.length
              }
              onClick={() => void quickCheckout("TARJETA")}
            >
              Tarjeta
            </button>
            <button
              disabled={
                busy || pendingCheckout ||
                !online ||
                !data.salesEnabled ||
                !data.shift ||
                !cart.length
              }
              onClick={() => void quickCheckout("TRANSFERENCIA")}
            >
              Transferencia
            </button>
          </div>
          <button
            className="charge main-charge"
            disabled={
              busy || pendingCheckout ||
              !online ||
              !data.salesEnabled ||
              !data.shift ||
              !cart.length ||
              total <= 0
            }
            onClick={() => setPaying(true)}
          >
            {!online
              ? "Esperando internet"
              : !data.shift
                ? "Abre la caja"
                : !data.salesEnabled
                  ? "Cobros aún desactivados"
                  : `Cobrar / dividir ${money(total)}`}
          </button>
          <button
            className="clear"
            disabled={!cart.length || pendingCheckout}
            onClick={() =>
              confirm("¿Vaciar la venta?") &&
              (setCart([]),
              setSaleDiscount({
                type: "MXN",
                value: 0,
                reason: "",
                authorizationId: "",
              }))
            }
          >
            Vaciar
          </button>
        </aside>
      </div>
      {paying && (
        <PaymentDialog
          data={data}
          total={total}
          onClose={() => setPaying(false)}
          onPaid={finishCheckout}
        />
      )}
      {tickets && data.shift && (
        <Tickets
          shiftId={data.shift.id}
          role={data.user.role}
          printer={data.printer}
          onClose={() => setTickets(false)}
        />
      )}
      {transfers && (
        <Transfers
          data={data}
          reload={load}
          close={() => setTransfers(false)}
        />
      )}
      {panelOpen && data.shift && (
        <ShiftPanel shiftId={data.shift.id} close={() => setPanelOpen(false)} />
      )}
    </main>
  );
}
